import React, { useState, useEffect } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getRequestData } from "@/api/dashboard";

interface RequestData {
  month: string;
  newRequests: number;
  completedRequests: number;
}

const RequestChart = () => {
  const [data, setData] = useState<RequestData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const requestData = await getRequestData();
        setData(requestData || []);
      } catch (error) {
        console.error("Error fetching request data:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const totalRequests = data.reduce(
    (acc, curr) =>
      acc + (curr.newRequests || 0) + (curr.completedRequests || 0),
    0
  );
  const totalNewRequests = data.reduce(
    (acc, curr) => acc + (curr.newRequests || 0),
    0
  );
  const totalCompletedRequests = data.reduce(
    (acc, curr) => acc + (curr.completedRequests || 0),
    0
  );

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            Request Overview
          </h2>
        </div>
        <div className="h-[300px] flex items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">
            Request Overview
          </h2>
        </div>
        <button className="text-gray-400 hover:text-gray-600">•••</button>
      </div>

      <div className="space-x-4 mb-6">
        <span className="inline-flex items-center">
          <span className="w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
          <span className="text-sm text-gray-600">New Requests</span>
        </span>
        <span className="inline-flex items-center">
          <span className="w-3 h-3 rounded-full bg-green-500 mr-2"></span>
          <span className="text-sm text-gray-600">Completed Requests</span>
        </span>
      </div>

      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 10, right: 0, left: -20, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#6B7280" }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#6B7280" }}
            />
            <Tooltip />
            <Area
              type="monotone"
              dataKey="completedRequests"
              stackId="1"
              stroke="none"
              fill="#10B981"
              fillOpacity={0.5}
            />
            <Area
              type="monotone"
              dataKey="newRequests"
              stackId="1"
              stroke="none"
              fill="#3B82F6"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-6">
        <div>
          <div className="text-2xl font-bold">
            {totalRequests.toLocaleString()}
          </div>
          <div className="text-sm text-gray-600 flex items-center">
            Total Requests
            <span className="text-green-500 ml-1">↑ 2.43%</span>
          </div>
        </div>
        <div>
          <div className="text-2xl font-bold">
            {totalNewRequests.toLocaleString()}
          </div>
          <div className="text-sm text-gray-600 flex items-center">
            New Requests
            <span className="text-green-500 ml-1">↑ 12.65%</span>
          </div>
        </div>
        <div>
          <div className="text-2xl font-bold">
            {totalCompletedRequests.toLocaleString()}
          </div>
          <div className="text-sm text-gray-600 flex items-center">
            Completed
            <span className="text-green-500 ml-1">↑ 5.62%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestChart;
